import { useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { buildTombstoneLookup, type TombstoneLookup } from '@bliks-io/ui';
import { apiClient } from '../client';
import { orgMemberKeys } from '../query-keys';
import { LOOKUP_LIST_CACHE } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import {
  listOrgMembersContract,
  showOrgMemberContract,
  type User,
  type UserStatus,
  type UserClassification,
  type ListOrgMembersResponse,
  type ShowOrgMemberResponse
} from '@bliks-io/contracts';

/**
 * Options for filtering organization members
 */
export interface UseOrgMembersOptions {
  /**
   * Filter users by status. Can be a single status or array of statuses.
   * When undefined, returns all users regardless of status.
   *
   * @example ['active'] - Only active users
   * @example ['active', 'pending'] - Active or pending users
   */
  status?: UserStatus | UserStatus[];

  /**
   * Filter users by department ID.
   * When provided, returns only users in that department.
   *
   * @example '507f1f77bcf86cd799439011' - Only users in specific department
   */
  departmentId?: string;

  /**
   * Filter users by classification. Can be a single classification or array.
   * When undefined, returns all users regardless of classification.
   *
   * @example 'internal' - Only internal members
   * @example ['internal', 'external'] - Both internal and external
   */
  classification?: UserClassification | readonly UserClassification[];

  /**
   * Opt into `homeOrgDeleted` on each item (identity pinned to a soft-deleted
   * tenant's org → sign-in locked). Costs extra super-admin-DB queries on the
   * backend, so only list pages that surface the lock warning should pass it
   * — never pickers.
   */
  withHomeOrgStatus?: boolean;

  /**
   * Opt into `signInResetAvailable` on each item (the caller may reset the
   * member's sign-in). Costs extra super-admin-DB queries on the backend, so
   * only the page that offers the reset should pass it.
   */
  withSignInReset?: boolean;
}

/**
 * Fetch organization members list with optional filtering
 *
 * @param options - Optional filters for the query
 *
 * @example
 * ```tsx
 * // Fetch all users
 * function OrgMembersList() {
 *   const { data: orgMembers, isLoading } = useOrgMembers();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!orgMembers) return null;
 *
 *   return <List items={orgMembers} />;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Fetch only active users (for assignments)
 * function ManagerSelect() {
 *   const { data: activeUsers } = useOrgMembers({ status: ['active'] });
 *   return <Select options={activeUsers} />;
 * }
 * ```
 */
export function useOrgMembers(
  options?: UseOrgMembersOptions,
  queryOverrides?: OrgMembersQueryOverrides
) {
  return useOrgMembersQuery(options, false, queryOverrides);
}

/**
 * Fetch org members INCLUDING soft-deleted ones, for resolving entity refs on
 * old recordings (assignees, who-else-does-this). Deleted users carry a
 * `deletedAt` timestamp so read surfaces can gray them out.
 *
 * Never use this for pickers/selectors — those must only offer live users
 * via {@link useOrgMembers}.
 */
export function useOrgMembersForLookup(options?: { enabled?: boolean }) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgMembersForLookupQueryOptions(),
    enabled: (options?.enabled ?? true) && !!token
  });
}

/**
 * {@link useOrgMembersForLookup} reduced to an id → tombstone map, for table
 * columns that gray out soft-deleted users (populated user refs never carry
 * `deletedAt` — the includeDeleted lookup is the only place it survives).
 */
export function useUserTombstoneLookup(): TombstoneLookup {
  const { data } = useOrgMembersForLookup();
  return useMemo(() => buildTombstoneLookup(data), [data]);
}

/**
 * Query options for the unfiltered lookup variant, shared between the hook
 * and route loaders that prefetch the lookup (e.g. the process editor) so
 * both sides stay on the same cache key — and on the same `gcTime`, which
 * only the lookup variant lengthens.
 */
export function orgMembersForLookupQueryOptions() {
  return queryOptions({
    ...orgMembersQueryOptions(undefined, true),
    ...LOOKUP_LIST_CACHE
  });
}

/**
 * Query options behind {@link useOrgMembers}, shared with route loaders that
 * prefetch a filtered list so both sides stay on the same cache key.
 */
export function orgMembersQueryOptions(options?: UseOrgMembersOptions, includeDeleted?: boolean) {
  return queryOptions({
    queryKey: orgMemberKeys.list(
      options?.status ||
        options?.departmentId ||
        options?.classification ||
        options?.withHomeOrgStatus ||
        options?.withSignInReset ||
        includeDeleted
        ? {
            status: options?.status,
            departmentId: options?.departmentId,
            classification: options?.classification,
            ...(options?.withHomeOrgStatus ? { withHomeOrgStatus: true } : {}),
            ...(options?.withSignInReset ? { withSignInReset: true } : {}),
            ...(includeDeleted ? { includeDeleted: true } : {})
          }
        : undefined
    ),
    queryFn: async ({ signal }): Promise<ListOrgMembersResponse> => {
      // Build query string for filters
      const queryParams = new URLSearchParams();
      if (options?.status) {
        const statuses = Array.isArray(options.status) ? options.status : [options.status];
        statuses.forEach((status) => queryParams.append('status', status));
      }
      if (options?.departmentId) {
        queryParams.append('departmentId', options.departmentId);
      }
      if (options?.classification) {
        const classifications = Array.isArray(options.classification)
          ? options.classification
          : [options.classification];
        classifications.forEach((c) => queryParams.append('classification', c));
      }
      if (options?.withHomeOrgStatus) {
        queryParams.append('withHomeOrgStatus', 'true');
      }
      if (options?.withSignInReset) {
        queryParams.append('withSignInReset', 'true');
      }
      if (includeDeleted) {
        queryParams.append('includeDeleted', 'true');
      }

      const path = queryParams.toString()
        ? `${listOrgMembersContract.path}?${queryParams.toString()}`
        : listOrgMembersContract.path;

      const { data } = await apiClient.get<ListOrgMembersResponse>(path, { signal });
      return data;
    }
  });
}

interface OrgMembersQueryOverrides {
  refetchOnMount?: boolean | 'always';
  enabled?: boolean;
}

function useOrgMembersQuery(
  options?: UseOrgMembersOptions,
  includeDeleted?: boolean,
  queryOverrides?: OrgMembersQueryOverrides
) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgMembersQueryOptions(options, includeDeleted),
    ...queryOverrides,
    enabled: !!token && (queryOverrides?.enabled ?? true)
  });
}

/**
 * Fetch only active organization members
 * Convenience hook for components that need assignable users (e.g., department managers, process owners)
 *
 * @example
 * ```tsx
 * function ManagerSelect() {
 *   const { data: activeUsers } = useActiveOrgMembers();
 *   return <Select options={activeUsers} />;
 * }
 * ```
 */
export function useActiveOrgMembers() {
  return useOrgMembers({ status: ['active'] });
}

export const orgMemberQueryOptions = (id: string) =>
  queryOptions({
    queryKey: orgMemberKeys.detail(id),
    queryFn: async ({ signal }): Promise<User> => {
      // Detail pages must load soft-deleted members too (direct URL,
      // links from old recordings); deleted ones carry deletedAt.
      const { data } = await apiClient.get<ShowOrgMemberResponse>(
        `${showOrgMemberContract.path.replace(':id', id)}?includeDeleted=true`,
        { signal }
      );
      return data.data;
    }
  });

/**
 * Fetch a single organization member by ID
 *
 * @example
 * ```tsx
 * function OrgMemberDetail({ id }: { id: string }) {
 *   const { data: orgMember, isLoading } = useOrgMember(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!orgMember) return <NotFound />;
 *
 *   return <DetailView orgMember={orgMember} />;
 * }
 * ```
 */
export function useOrgMember(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...orgMemberQueryOptions(id),
    enabled: !!token && !!id
  });
}
